import { useState, useEffect, useRef, useCallback } from "react";
import {
  formattedDate,
  formattedTime,
} from "../../components/formatted/FormattedDateAndTime";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import { CropImage } from "../../components/utils/ImageTreatment";
import CloseIcon from "@mui/icons-material/Close";
import i18n from "i18next";
import { UI_CONSTANTS } from "../../constants/ui";
const ActivityHistoryTimeLineRow = ({
  timeLine,
  index,
  resolveHistoryFlux,
}) => {
  const { t } = useTranslation();

  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const imageContainerRef = useRef(null);
  const zoomModalRef = useRef(null);

  const fluxLabel = resolveHistoryFlux?.(timeLine) ?? timeLine.wasteName ?? "";

  const refetchOutputSurface = useCallback(async () => {}, []);

  const formatDateForZoom = (dateString) => {
    const date = new Date(dateString);
    const language = i18n.language || "fr-FR";

    const day = date.getDate();
    const month = date.toLocaleDateString(language, { month: "long" });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    if (language.startsWith("fr")) {
      return `${day} ${month} ${year}, ${hours}h${minutes}`;
    } else if (language.startsWith("en")) {
      const monthName = date.toLocaleDateString("en-US", { month: "long" });
      return `${monthName} ${day}, ${year}, ${hours}:${minutes}`;
    } else if (language.startsWith("es")) {
      const monthName = date.toLocaleDateString("es-ES", { month: "long" });
      return `${day} de ${monthName} de ${year}, ${hours}:${minutes}`;
    } else {
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    }
  };

  const handleImageClick = () => {
    if (timeLine.image_url) {
      setIsImageZoomed(true);
    }
  };

  const handleCloseZoom = useCallback(() => {
    setIsImageZoomed(false);
  }, []);

  useEffect(() => {
    if (!isImageZoomed) {
      return;
    }

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        handleCloseZoom();
      }
    };

    const handleClickOutside = (e) => {
      if (
        zoomModalRef.current &&
        !zoomModalRef.current.contains(e.target) &&
        !imageContainerRef.current?.contains(e.target)
      ) {
        handleCloseZoom();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = previousOverflow;
    };
  }, [isImageZoomed, handleCloseZoom]);

  return (
    <section className="table-block">
      <article className="table-line table-line-no-hover">
        <div className="data-table data-activity-image">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              ref={imageContainerRef}
              style={{
                width: "112px",
                height: "112px",
                border: "1px solid #D1D5DB",
                borderRadius: "4px",
                padding: "4px",
                backgroundColor: "#FFFFFF",
                overflow: "hidden",
                flexShrink: 0,
                cursor: "pointer",
                position: "relative",
              }}
              onClick={handleImageClick}
              onMouseEnter={() => setIsHoveringImage(true)}
              onMouseLeave={() => setIsHoveringImage(false)}
            >
              {timeLine.image_url ? (
                <>
                  <CropImage
                    container={timeLine}
                    data={timeLine}
                    containersSelected={[]}
                    setContainersSelected={() => {}}
                    imgDivWidth={UI_CONSTANTS.IMAGES.THUMBNAIL_INNER_SIZE}
                    imgDivHeight={UI_CONSTANTS.IMAGES.THUMBNAIL_INNER_SIZE}
                    isTooltip={false}
                    isMap={false}
                    isCharacterization={false}
                    displayLegend={false}
                    materialsDropdownToDisplay={[]}
                    showCheckbox={false}
                    onClickZoomButton={() => {}}
                    displayToBlock={false}
                    setContainerActive={() => {}}
                    onAfterSegmentationUpdate={refetchOutputSurface}
                  />
                  {isHoveringImage && (
                    <>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "white",
                          opacity: 0.2,
                          pointerEvents: "none",
                          zIndex: 9,
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "45px",
                          height: "45px",
                          borderRadius: "30px",
                          backgroundColor: "grey",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                          zIndex: 10,
                        }}
                      >
                        <span
                          style={{
                            color: "white",
                            fontSize: "35px",
                            fontWeight: "normal",
                            lineHeight: 1,
                            marginTop: "-2px",
                          }}
                        >
                          +
                        </span>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="fas fa-image" style={{ color: "#9CA3AF" }}></i>
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <span
                className="font-semibold truncate"
                style={{ marginBottom: "2px" }}
              >
                {resolveHistoryFlux == null ? (
                  <span style={{ color: "#9CA3AF" }}>...</span>
                ) : (
                  fluxLabel
                )}
              </span>
              <span style={{ fontSize: "0.875rem", color: "#6B7280" }}>
                {formattedDate(timeLine._time, t)},{" "}
                {formattedTime(timeLine._time)}
              </span>
            </div>
          </div>
        </div>

        <div className="data-table data-activity-address">
          <span className="font-semibold truncate">{timeLine.addressName}</span>
          {timeLine.positioning && (
            <span
              style={{
                display: "block",
                fontSize: "0.875rem",
                color: "#6B7280",
              }}
            >
              {timeLine.positioning.charAt(0).toUpperCase() +
                timeLine.positioning.slice(1)}
            </span>
          )}
        </div>

        <div
          className="data-table data-activity-packmat"
          style={{ justifyContent: "flex-end", textAlign: "right" }}
        >
          {timeLine.packmat === true ? t("Yes") : "-"}
        </div>

        <div
          className="data-table data-activity-anomalies"
          style={{ justifyContent: "flex-end", textAlign: "right" }}
        >
          {timeLine.anomalies > 0 ? <span>{timeLine.anomalies}</span> : "-"}
        </div>

        <div
          className="data-table data-activity-level"
          style={{
            justifyContent: "flex-end",
            textAlign: "right",
            paddingRight: "16px",
          }}
        >
          {timeLine.activatedFillingLevelOption ? (
            <span>{timeLine.filling_level}%</span>
          ) : (
            <Tooltip title={t("filling_level_config_required")}>
              <InfoIcon style={{ color: "#888", cursor: "pointer" }} />
            </Tooltip>
          )}
        </div>
      </article>

      {isImageZoomed && timeLine.image_url && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            ref={zoomModalRef}
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "90vw",
              maxHeight: "90vh",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "16px",
                left: "24px",
                fontSize: "28px",
                color: "#222222",
                fontWeight: 500,
                zIndex: 10,
              }}
            >
              {formatDateForZoom(timeLine._time)}
            </div>
            <button
              onClick={handleCloseZoom}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                width: "32px",
                height: "32px",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <CloseIcon
                style={{
                  width: "24px",
                  height: "24px",
                  color: "#000000",
                }}
              />
            </button>
            <div
              style={{
                marginTop: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: "80vw",
                maxHeight: "80vh",
              }}
            >
              <CropImage
                container={timeLine}
                data={timeLine}
                containersSelected={[]}
                setContainersSelected={() => {}}
                imgDivWidth={Math.min(window.innerWidth * 0.7, 1200)}
                imgDivHeight={Math.min(window.innerHeight * 0.7, 800)}
                isTooltip={false}
                isMap={false}
                isCharacterization={false}
                displayLegend={false}
                materialsDropdownToDisplay={[]}
                showCheckbox={false}
                onClickZoomButton={() => {}}
                displayToBlock={false}
                setContainerActive={() => {}}
                onAfterSegmentationUpdate={refetchOutputSurface}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ActivityHistoryTimeLineRow;
